/* Step 3 — Triage. Ported from the prototype's triageBody(). Weighted scorecard
   driven by the profile weights, thesis-fit checks, an Advance/Hold/Decline
   recommendation and an editable AI-drafted rationale. Data loads from
   GET /deal/:id/triage; the rationale + recommendation persist via PUT, and
   advancing calls POST .../triage/advance (both append to the FCA decision log). */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import DeclineModal from '../../components/DeclineModal';
import { Loading, EmptyState } from '../../components/States';
import { useToast } from '../../components/Toast';
import { useProfile } from '../../state';
import { BASE } from '../../helpers';
import { fetchTriage, saveTriage, runTriage, advanceTriage, ok } from '../../api';

const RECS = [
	['advance', 'var(--dlg-green)', 'Advance', 'Move to diligence'],
	['hold', 'var(--dlg-amber)', 'Hold', 'Request more info'],
	['decline', 'var(--dlg-red)', 'Decline', 'Send templated pass note'],
];

export default function Triage({ deal }) {
	const navigate = useNavigate();
	const showToast = useToast();
	const { profile } = useProfile();
	const [T, setT] = useState(null);
	const [status, setStatus] = useState('loading');   // loading | generating | ready | noanalysis
	const [rec, setRec] = useState('advance');
	const [rationale, setRationale] = useState('');
	const [declineOpen, setDeclineOpen] = useState(false);
	const [advancing, setAdvancing] = useState(false);
	const [regenerating, setRegenerating] = useState(false);

	const applyTriage = (data) => { setT(data); setRationale(data.rationale || ''); setRec(data.recommendation || 'advance'); setStatus('ready'); };

	// No triage stored yet — ask Intelligentsia to score it (needs a completed analysis).
	const generate = useCallback(async () => {
		setStatus('generating');
		const res = await runTriage(deal.id);
		if (ok(res) && res.body) applyTriage(res.body);
		else setStatus('noanalysis');
	}, [deal.id]);

	const load = useCallback(async () => {
		setStatus('loading'); setT(null);
		const data = await fetchTriage(deal.id);
		if (data) { applyTriage(data); return; }
		await generate();
	}, [deal.id, generate]);
	useEffect(() => { load(); }, [load]);

	const regenerate = async () => {
		if (regenerating) return;
		setRegenerating(true);
		const res = await runTriage(deal.id);
		setRegenerating(false);
		if (ok(res) && res.body) { applyTriage(res.body); showToast('Triage re-scored by Intelligentsia'); }
		else showToast((res && res.message) || 'Could not regenerate triage');
	};

	if (status === 'loading') return (<main className="page screen"><Loading label="Loading triage…" /></main>);
	if (status === 'generating') return (<main className="page screen"><Loading label="Intelligentsia is scoring the deal…" /></main>);
	if (status === 'noanalysis' || !T) return (<main className="page screen"><EmptyState title="Run the analysis first" sub="Triage scores the deal against your thesis weights — it needs the AI analysis to be complete." /></main>);

	const rows = T.rows || [];
	const fit = T.fit || [];
	const firmShort = (profile && profile.short) ? profile.short.split(' ')[0] : 'the fund';

	const advance = async () => {
		if (advancing) return;
		setAdvancing(true);
		await saveTriage(deal.id, { rationale, recommendation: rec });   // persist edits first
		const res = await advanceTriage(deal.id);
		setAdvancing(false);
		if (!ok(res)) return;
		showToast('Advanced to diligence — decision logged');
		navigate(`${BASE}/deal/${deal.id}/diligence`);
	};

	return (
		<main className="page screen">
			<div className="kicker">Step 3 · Triage</div>
			<div className="page__title" style={{ fontSize: 24, marginBottom: 16 }}>{deal.name} — advance, hold, or decline</div>

			<div className="grid grid--split">
				<div className="card">
					<div className="card__label" style={{ marginBottom: 8 }}>Scorecard vs {firmShort} thesis</div>
					{rows.map(r => (
						<div key={r.name} className="scorerow">
							<span className="scorerow__name">{r.name}</span>
							<div className="scorerow__bar"><div className="scorerow__fill" style={{ width: `${r.score * 10}%` }} /></div>
							<span className="scorerow__pct">{r.pct}%</span>
							<span className="scorerow__score">{Number(r.score).toFixed(1)}</span>
						</div>
					))}
					<div className="weighted"><strong>Weighted score</strong><span className="weighted__num">{T.weighted} / 10</span></div>
					<div className="card__foot">Weights come straight from your Investor Profile — change them there and every triage rescores.</div>
				</div>
				<div>
					<div className="card" style={{ marginBottom: 12 }}>
						<div className="card__label" style={{ marginBottom: 8 }}>Thesis fit</div>
						{fit.map((f, i) => (
							<div key={i} className="finding"><span className="finding__dot" style={{ background: f.level === 'green' ? 'var(--dlg-green)' : 'var(--dlg-amber)' }} /><span>{f.text}</span></div>
						))}
					</div>
					<div className="card">
						<div className="card__label" style={{ marginBottom: 10 }}>Recommendation</div>
						{RECS.map(([id, dot, title, sub]) => (
							<div key={id} className={`rec ${id === rec ? 'is-selected' : ''}`} onClick={() => setRec(id)}>
								<div className="rec__title"><span className="finding__dot" style={{ background: dot }} />{title}</div>
								<div className="rec__sub">{sub}</div>
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="card">
				<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
					<span className="chip chip--blue">✦ Rationale drafted by Intelligentsia — edit before saving</span>
					<span className="cp" style={{ fontSize: 12.5, color: 'var(--dlg-muted)' }} onClick={regenerate}>{regenerating ? '⟲ regenerating…' : '⟲ regenerate'}</span>
				</div>
				<textarea className="rationale-box" value={rationale} onChange={e => setRationale(e.target.value)} />
				<div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
					<button className="pill pill--green" onClick={advance} disabled={advancing}>{advancing ? 'Advancing…' : 'Advance to diligence →'}</button>
					<button className="pill pill--declinered" onClick={() => setDeclineOpen(true)}>Decline &amp; notify</button>
					<span style={{ fontSize: 12.5, color: 'var(--dlg-muted)' }}>Both outcomes are logged to the FCA-format decision log. Declining sends the templated pass note — editable before it goes.</span>
				</div>
			</div>

			{declineOpen && <DeclineModal deal={deal} context="triage" onClose={() => setDeclineOpen(false)} onDeclined={() => navigate(`${BASE}/pipeline`)} />}
		</main>
	);
}
